/* eslint-env browser */
import { Controller } from "stimulus";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = [
    "actionsBar",
    "filteringActions",
    "flaggingDropdown",
    "issueCountContainer",
    "resolvedIssueCountContainer",
    "resolvePopover",
    "selectedActions",
    "selectedItemCount",
    "selectedIssueCount",
    "selectedResolvedIssueCount",
  ];

  static values = {
    flaggingMode: { type: Boolean, default: false },
    flaggingType: String,
    translatedStrings: Object,
  };

  allocationReceived = debounce(this.#allocationReceived.bind(this), 10);
  controllerStopped = false;
  allocationChanges = 0;
  issueChanges = 0;
  resolvedIssueChanges = 0;

  connect() {
    this.connectControllers();
  }

  disconnect() {
    this.controllerStopped = true;
  }

  flaggingTypeValueChanged() {
    this.dispatch("update", {
      prefix: "flagging-mode",
      detail: { enabled: this.flaggingModeValue, type: this.flaggingTypeValue },
    });
  }

  cancelSelections() {
    this.dispatch("receivingReset", { prefix: false });

    this.resetFlaggingMode();
    this.actionsBarTarget.dataset.display = "filters";
  }

  configureToolbar() {
    const selected = Math.abs(this.allocationChanges) === 1 ? this.i18n.selectedOne : this.i18n.selectedMany;
    const issues = Math.abs(this.issueChanges) === 1 ? this.i18n.issue : this.i18n.issues;
    const resolved = Math.abs(this.resolvedIssueChanges) === 1 ? this.i18n.resolve : this.i18n.resolves;

    if (this.hasIssueCountContainerTarget) {
      this.issueCountContainerTarget.classList.toggle("hidden", this.issueChanges === 0);
      this.selectedIssueCountTarget.textContent = this.issueChanges ? `${Math.abs(this.issueChanges)} ${issues}` : "";
    }

    if (this.hasResolvedIssueCountContainerTarget) {
      this.resolvedIssueCountContainerTarget.classList.toggle("hidden", this.resolvedIssueChanges === 0);
      this.selectedResolvedIssueCountTarget.textContent = this.resolvedIssueChanges
        ? `${Math.abs(this.resolvedIssueChanges)} ${resolved}`
        : "";
    }

    this.selectedItemCountTarget.textContent = `${Math.abs(this.allocationChanges)} ${selected}`;
    this.actionsBarTarget.dataset.display =
      this.allocationChanges || this.issueChanges || this.resolvedIssueChanges || this.flaggingModeValue
        ? "selected"
        : "filters";
  }

  #allocationReceived() {
    setTimeout(() => {
      this.allocationChanges = 0;
      this.issueChanges = 0;
      this.resolvedIssueChanges = 0;
      this.dirtyFields.forEach((controller) => {
        const changes = controller.countChanges;
        this.allocationChanges += changes.allocationChanges;
        this.issueChanges += changes.issueChanges;
        this.resolvedIssueChanges += changes.resolvedChanges;
      });

      this.configureToolbar();
    });
  }

  flaggingModeSelected(event) {
    const { value } = event.detail;

    this.flaggingModeValue = value !== "";
    this.flaggingTypeValue = value ?? "";

    this.flaggingDropdownTargets.forEach((element) => {
      element.querySelector("[value=reset]")?.classList.toggle("hidden", !this.flaggingModeValue);
      element.querySelector("[role=afterButtonIcon]")?.classList.toggle("hidden", this.flaggingModeValue);

      this.application
        .getControllerForElementAndIdentifier(element, "ui--dropdown--dropdown")
        .updateSelectedValue(event);
    });

    this.configureToolbar();
  }

  resetFlaggingMode() {
    this.flaggingModeValue = false;
    this.flaggingTypeValue = "";

    this.flaggingDropdownTargets.forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(element, "ui--dropdown--dropdown");

      controller.selectionChangedValue = this.flaggingModeValue;
      controller.selectedValue = this.flaggingTypeValue;

      element.querySelector("[value=reset]")?.classList.add("hidden");
      element.querySelector("[role=afterButtonIcon]")?.classList.remove("hidden");

      controller.updateSelectedValue({
        detail: {
          value: this.flaggingTypeValue,
          selectionChanged: this.flaggingModeValue,
        },
      });
    });

    this.configureToolbar();
  }

  submit() {
    setTimeout(() => {
      // reset everything after the submit is made
      this.dispatch("receivingRefresh", { prefix: false });

      this.resetFlaggingMode();
      this.actionsBarTarget.dataset.display = "filters";
    });
  }

  connectControllers() {
    const main = Array.from(this.element.querySelectorAll("[data-control=main][data-controller-delay]"));
    const hidden = Array.from(this.element.querySelectorAll(":not([data-control=main])[data-controller-delay]"));

    const delay = [...main, ...hidden];
    if (delay.length > 0) {
      const loader = (callNext = true) => {
        for (let i = 0; i < 5; i += 1) {
          const element = delay.shift();
          if (!element) return;

          const { controllerDelay, controller } = element.dataset;
          const delayedControllers = controllerDelay.split(" ");
          const dataControllers = controller || "";
          const controllerToAdd = delayedControllers.filter(
            (delayedController) => delayedController && !dataControllers.includes(delayedController),
          );

          if (controllerToAdd.length > 0) {
            element.dataset.controller = `${dataControllers} ${controllerToAdd[0]}`.trim();
            if (controllerToAdd.length > 1) {
              delay.push(element);
            }
          }
        }

        if (callNext && !this.controllerStopped) {
          setTimeout(loader);
        }
      };

      // prevent too many visual updates on the initial show
      for (let i = 0; i < 5; i += 1) {
        loader(false);
      }
      loader();
    }
  }

  get dirtyFields() {
    return [
      ...this.element.querySelectorAll(":not([data-control=main])[data-goods--receivable-good-dirty-value=true]"),
    ].map((element) => this.application.getControllerForElementAndIdentifier(element, "goods--receivable-good"));
  }

  get i18n() {
    return this.translatedStringsValue;
  }
}
